import { useEffect, useState } from "react";
import { useCountdown } from "shared/model";
import {
  useStartTelegramLinkMutation,
  useGetTelegramLinkStatusQuery,
} from "../api/twoFactorAuthApi";
import { getErrorStatusCode, getTelegramLinkError } from "./getTelegramLinkError";

const POLL_INTERVAL_MS = 2000;
const TOKEN_NOT_FOUND = 404;

export type TelegramLinkPhase =
  | "loading"
  | "waiting"
  | "confirmed"
  | "expired"
  | "error";

interface UseTelegramLinkResult {
  phase: TelegramLinkPhase;
  deepLink: string | null;
  secondsLeft: number;
  errorMessage: string | null;
  retry: () => void;
}

export const useTelegramLink = (
  onConfirmed: () => void,
): UseTelegramLinkResult => {
  const [startLink, { data: startData, error: startError, isLoading }] =
    useStartTelegramLinkMutation();
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    startLink();
    setSettled(false);
  }, [startLink]);

  const token = startData?.token ?? "";
  const expiresAt = startData ? new Date(startData.expires_at).getTime() : 0;
  const secondsLeft = useCountdown(expiresAt);
  const timedOut = expiresAt > 0 && secondsLeft <= 0;

  const { data: statusData, error: statusError } = useGetTelegramLinkStatusQuery(
    token,
    {
      skip: !token || timedOut || settled,
      pollingInterval: POLL_INTERVAL_MS,
    },
  );
  const tokenNotFound = getErrorStatusCode(statusError) === TOKEN_NOT_FOUND;

  useEffect(() => {
    if (statusData?.status === "confirmed") {
      setSettled(true);
      onConfirmed();
    } else if (statusData?.status === "expired" || statusError) {
      setSettled(true);
    }
  }, [statusData?.status, statusError, onConfirmed]);

  const phase: TelegramLinkPhase = isLoading
    ? "loading"
    : startError
      ? "error"
      : statusData?.status === "confirmed"
        ? "confirmed"
        : timedOut || statusData?.status === "expired" || tokenNotFound
          ? "expired"
          : statusError
            ? "error"
            : "waiting";

  const retry = () => {
    setSettled(false);
    startLink();
  };

  return {
    phase,
    deepLink: startData?.deep_link ?? null,
    secondsLeft,
    errorMessage: getTelegramLinkError(startError ?? statusError),
    retry,
  };
};
